import { ReactElement } from "react"
import BaseTable, { AutoResizer, Column, ColumnShape } from "react-base-table"

import { ContextMenu, ContextMenuItem, Tag } from "@wfp/ui"

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faEye, faSquareCaretDown, IconDefinition } from "@fortawesome/free-solid-svg-icons"

import { ReportTableData } from "../../interfaces/dataInterfaces"
import { formatDate } from "../../utils/helpers"

import PermissionWrapper from "../Shared/Common/PermissionWrapper"
import { MARKET_MODULE } from "../../constants/constants"
import { CommodityPriceStatus } from "../../constants/prices"

type Props = {
  tableHeight: string
  data?: ReportTableData[]
  setShowDetails: (data: ReportTableData | null) => void
}

const permissionsProps = {
  moduleId: MARKET_MODULE,
  subModule: "reports",
}

const tagType = {
  [CommodityPriceStatus.ApprovedInternal]: "success",
  [CommodityPriceStatus.Approved]: "success",
  [CommodityPriceStatus.Rejected]: "error",
}

const getStatusLabel = (status: CommodityPriceStatus): string => {
  if (status === CommodityPriceStatus.ApprovedInternal) return "Internal Use Only"
  return CommodityPriceStatus[status] ?? ""
}

const ReportTable = ({ tableHeight, data, setShowDetails }: Props): ReactElement => {
  const RenderContextItem = ({
    onAction,
    feature,
    label,
    icon,
    color,
    disabled = false,
  }: {
    onAction: () => void
    feature: string
    label: string
    icon: IconDefinition
    color: string
    disabled?: boolean
  }): ReactElement | null => {
    if (disabled) return null
    return (
      <PermissionWrapper {...permissionsProps} feature={feature}>
        <ContextMenuItem className="p-1.5 px-2 text-left w-40 hover:bg-slate-200" onClick={onAction}>
          <div>
            <FontAwesomeIcon icon={icon} color={color} className="mr-1" />
            <span style={{ color }}>{label}</span>
          </div>
        </ContextMenuItem>
      </PermissionWrapper>
    )
  }

  const columns: ColumnShape<ReportTableData>[] = [
    {
      key: "commodityPriceStatusId",
      dataKey: "commodityPriceStatusId",
      title: "Status",
      width: 150,
      maxWidth: 150,
      minWidth: 150,
      cellRenderer: ({ rowData }) => {
        return (
          <Tag type={tagType[rowData.commodityPriceStatusId] ?? "info"} className="font-bold">
            {getStatusLabel(rowData.commodityPriceStatusId)}
          </Tag>
        )
      },
    },
    {
      key: "commodityPriceDate",
      title: "Date",
      width: 160,
      cellRenderer: ({ rowData }) => formatDate(rowData.commodityPriceDate, "DD-MMM-YYYY"),
    },
    {
      key: "marketName",
      dataKey: "marketName",
      title: "Market",
      width: 0,
      flexGrow: 2,
      resizable: true,
    },
    {
      key: "commodityName",
      dataKey: "commodityName",
      title: "Commodity",
      width: 0,
      flexGrow: 1,
      resizable: true,
    },
    {
      key: "priceTypeName",
      dataKey: "priceTypeName",
      title: "Price Type",
      width: 100,
      resizable: true,
    },
    {
      key: "commodityUnitName",
      dataKey: "commodityUnitName",
      title: "Unit",
      width: 80,
      resizable: true,
    },
    {
      key: "commodityPriceValue",
      dataKey: "commodityPriceValue",
      title: "Price",
      width: 100,
      resizable: true,
      cellRenderer: ({ rowData }) => rowData.commodityPriceValue?.toLocaleString(),
    },
    {
      key: "commodityPriceSourceName",
      dataKey: "commodityPriceSourceName",
      title: "Source",
      resizable: true,
      width: 0,
      flexGrow: 1,
    },
    {
      key: "action",
      width: 60,
      align: Column.Alignment.CENTER,
      maxWidth: 60,
      flexGrow: 0,
      cellRenderer: ({ rowData }) => (
        <div
          aria-hidden
          className="d-flex align-items-center justify-end"
          onClick={e => {
            e.preventDefault()
            e.stopPropagation()
          }}
        >
          <ContextMenu
            content={
              <RenderContextItem
                label="Details"
                icon={faEye}
                color="var(--interactive-01)"
                feature="details"
                onAction={() => setShowDetails(rowData)}
              />
            }
          >
            <div className="cursor-pointer">
              <FontAwesomeIcon icon={faSquareCaretDown} color="var(--interactive-01)" size="xl" />
            </div>
          </ContextMenu>
        </div>
      ),
    },
  ]

  return (
    <div className="h-full w-full" style={{ height: tableHeight ?? "50vh" }}>
      <AutoResizer>
        {({ width, height }) => (
          <BaseTable
            data={data?.map(itemData => ({
              ...itemData,
              rowId: `${itemData.commodityPriceDate}-${itemData.seriesCombinationCode}-${itemData.marketId}`,
            }))}
            width={width}
            height={height}
            rowKey="rowId"
            rowHeight={38}
            headerHeight={45}
            className="custom-table"
            columns={columns}
            emptyRenderer={() => <p>No records to display</p>}
          />
        )}
      </AutoResizer>
    </div>
  )
}

export default ReportTable
